import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type IndicatorStyle = 'up-down' | 'right-down';
type Variant = 'default' | 'compact';

interface OverviewSectionCardProps {
  id?: string;
  number: number;
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  children?: ReactNode;
  headerActions?: ReactNode;
  disableToggle?: boolean;
  indicatorStyle?: IndicatorStyle;
  variant?: Variant;
  className?: string;
}

export function OverviewSectionCard({
  id,
  number,
  title,
  collapsed,
  onToggle,
  children,
  headerActions,
  disableToggle = false,
  indicatorStyle = 'up-down',
  variant = 'default',
  className,
}: OverviewSectionCardProps) {
  const isCompact = variant === 'compact';

  return (
    <div
      id={id}
      className={cn(
        isCompact
          ? 'bg-white rounded-lg border border-slate-200 overflow-hidden'
          : 'bg-white rounded-lg border border-slate-200 overflow-hidden scroll-mt-4',
        className
      )}
    >
      <div className={cn(isCompact ? '' : 'px-6 py-4 border-b border-slate-100')}>
        <div className={cn('flex items-center', isCompact ? '' : 'justify-between gap-4')}>
          <button
            onClick={onToggle}
            className={cn(
              'group',
              isCompact
                ? 'w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors'
                : 'flex-1 flex items-center gap-3 text-left'
            )}
            disabled={disableToggle}
          >
            <div className={cn('flex items-center gap-2', isCompact ? '' : 'w-full')}>
              <div
                className={cn(
                  'shrink-0 flex items-center justify-center transition-colors',
                  isCompact
                    ? 'w-6 h-6 rounded-sm bg-slate-200'
                    : 'w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-slate-200'
                )}
              >
                <span className={cn('font-semibold text-slate-700', isCompact ? 'text-xs' : 'text-sm')}>{number}</span>
              </div>
              <h4
                className={cn(
                  'text-slate-900 transition-colors',
                  isCompact
                    ? 'flex-1 text-left text-sm font-semibold'
                    : 'text-lg font-semibold group-hover:text-slate-700'
                )}
              >
                {title}
              </h4>
              {!disableToggle && (
                <div className={cn(!isCompact && 'ml-auto')}>
                  {collapsed ? (
                    indicatorStyle === 'up-down' ? (
                      <ChevronDown className={cn(isCompact ? 'h-4 w-4 text-slate-600' : 'h-5 w-5 text-slate-400')} />
                    ) : (
                      <ChevronRight className={cn(isCompact ? 'h-4 w-4 text-slate-600' : 'h-5 w-5 text-slate-400')} />
                    )
                  ) : indicatorStyle === 'up-down' ? (
                    <ChevronUp className={cn(isCompact ? 'h-4 w-4 text-slate-600' : 'h-5 w-5 text-slate-400')} />
                  ) : (
                    <ChevronDown className={cn(isCompact ? 'h-4 w-4 text-slate-600' : 'h-5 w-5 text-slate-400')} />
                  )}
                </div>
              )}
            </div>
          </button>
          {!collapsed && headerActions}
        </div>
      </div>

      {!collapsed && children && (
        <div className={cn(isCompact ? 'px-4 py-3 border-t border-slate-100' : 'p-6')}>{children}</div>
      )}
    </div>
  );
}
